import type { TricksFile } from "../config/types";

export interface ActiveTrick {
    name: string;
    elapsed: number;
}

export interface AirTricks {
    active: ActiveTrick | null;
    done: string[];
    spinDeg: number;
    spinsScored: number;
}

export interface CompletedTrick {
    trick: string;
    value: number;
}

export type Landing = { clean: true } | { clean: false; reason: string };

export const NO_TRICKS: AirTricks = { active: null, done: [], spinDeg: 0, spinsScored: 0 };

const SPIN = "spin180";

export function pickTrick(roll: number, cfg: TricksFile): string {
    const names = Object.keys(cfg.tricks);
    return names[Math.min(names.length - 1, Math.floor(roll * names.length))];
}

export function pressTrick(t: AirTricks, roll: number, cfg: TricksFile): { state: AirTricks; started: string | null } {
    if (t.active) return { state: t, started: null };
    const name = pickTrick(roll, cfg);
    return { state: { ...t, active: { name, elapsed: 0 } }, started: name };
}

export function stepTricks(t: AirTricks, dt: number, spin: number, cfg: TricksFile): { state: AirTricks; completed: CompletedTrick[] } {
    const completed: CompletedTrick[] = [];
    let { active, done } = t;

    if (active) {
        active = { ...active, elapsed: active.elapsed + dt };
        if (active.elapsed >= cfg.trickDuration) {
            completed.push({ trick: active.name, value: cfg.tricks[active.name].value });
            done = [...done, active.name];
            active = null;
        }
    }

    const spinDeg = t.spinDeg + spin * cfg.air.spinDegPerS * dt;
    const spins = Math.floor(Math.abs(spinDeg) / 180);
    for (let i = t.spinsScored; i < spins; i++) completed.push({ trick: SPIN, value: cfg.spin180Value });

    return { state: { active, done, spinDeg, spinsScored: Math.max(spins, t.spinsScored) }, completed };
}

export function trickProgress(active: ActiveTrick, cfg: TricksFile): number {
    return Math.min(1, active.elapsed / cfg.trickDuration);
}

export function trickValue(t: AirTricks, cfg: TricksFile): number {
    return t.done.reduce((sum, name) => sum + cfg.tricks[name].value, 0) + t.spinsScored * cfg.spin180Value;
}

function facingOff(spinDeg: number): number {
    const facing = ((spinDeg % 360) + 360) % 360;
    return Math.min(facing, 360 - facing);
}

export function classifyLanding(t: AirTricks, headingDeg: number, checkHeading: boolean, cfg: TricksFile): Landing {
    if (t.active) return { clean: false, reason: t.active.name };
    if (facingOff(t.spinDeg) > cfg.cleanLandingAngle) return { clean: false, reason: SPIN };
    if (checkHeading && Math.abs(headingDeg) > cfg.cleanLandingAngle) return { clean: false, reason: "sideways" };
    return { clean: true };
}
